import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sun, Cloud, CloudRain, CloudSnow, CloudLightning, CloudFog, Umbrella, CalendarClock, Loader2 } from "lucide-react";
import api from "../lib/api";

const ICONS = { clear: Sun, cloud: Cloud, rain: CloudRain, snow: CloudSnow, storm: CloudLightning, fog: CloudFog };

export function useEveningWeather(center, date) {
  const [weather, setWeather] = useState(null);
  useEffect(() => {
    if (!center?.lat || !date) return;
    let cancelled = false;
    setWeather(null);
    api
      .get("/weather", { params: { lat: center.lat, lon: center.lon, date } })
      .then(({ data }) => !cancelled && setWeather(data))
      .catch(() => !cancelled && setWeather({ available: false, reason: "Weather unavailable." }));
    return () => {
      cancelled = true;
    };
  }, [center?.lat, center?.lon, date]);
  return weather;
}

export function WeatherBadge({ weather, outdoorCount }) {
  if (weather === null) {
    return (
      <span data-testid="weather-loading" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <Loader2 size={13} className="animate-spin" /> Checking the forecast…
      </span>
    );
  }
  if (!weather.available) {
    return (
      <span
        data-testid="weather-unavailable"
        className="inline-flex items-center gap-1.5 rounded-full border border-border px-2.5 py-0.5 text-xs text-muted-foreground"
      >
        <CalendarClock size={13} /> {weather.reason}
      </span>
    );
  }
  const Icon = ICONS[weather.icon] || Cloud;
  const rainy = weather.rain_likely;
  return (
    <motion.span
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="weather-badge"
      className={`inline-flex flex-wrap items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${
        rainy ? "border-sky-400/50 bg-sky-500/10 text-foreground" : "border-border bg-secondary/60 text-foreground"
      }`}
    >
      <Icon size={14} className={rainy ? "text-sky-400" : "text-amber-400"} />
      <span data-testid="weather-condition">{weather.condition}</span>
      <span className="text-muted-foreground">·</span>
      <span data-testid="weather-temp">{weather.temp_low_f}–{weather.temp_high_f}°F</span>
      <span className="text-muted-foreground">·</span>
      <span data-testid="weather-rain" className={rainy ? "text-sky-400" : "text-muted-foreground"}>
        <Umbrella size={12} className="inline -mt-0.5 mr-1" />
        {weather.rain_chance}% rain
      </span>
      {rainy && outdoorCount > 0 && (
        <span data-testid="weather-outdoor-warning" className="ml-1 rounded-full bg-sky-500/20 px-2 py-0.5 text-sky-300">
          {outdoorCount} outdoor stop{outdoorCount > 1 ? "s" : ""} flagged
        </span>
      )}
    </motion.span>
  );
}
